import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@tripian/core";
import Model from "@tripian/model";
import moment from "moment";
import useTranslate from "../../../hooks/useTranslate";

interface ItineraryData {
    tripHash: string;
    title: string;
    subtitle: string;
    image: string;
    alt: string;
    howManyDays: number;
}

const FeaturedItineraries = () => {
    const navigate = useNavigate();
    const [itineraries, setItineraries] = useState<ItineraryData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const { t } = useTranslate();
    useEffect(() => {
        const fetchFeaturedItineraries = async () => {
            const featuredHashes = window.tconfig.FEATURED_ITINERARIES || [];
            if (featuredHashes.length === 0) {
                setLoading(false);
                return;
            }

            try {
                const itineraryPromises = featuredHashes.map(async (tripHash) => {
                    try {
                        const tripRef: Model.TimelineTrip = await api.timelineGetShared(tripHash);

                        // Get city image from city object
                        const cityImage = tripRef.city.image?.url || tripRef.city.image?.url || "";
                        const segments = tripRef.tripProfile?.segments || [];
                        const firstSegment = moment(segments[0].startDate).utcOffset(0);
                        const lastSegment = moment(segments[segments.length - 1].endDate).utcOffset(0);

                        const start = moment(firstSegment?.format('YYYY-MM-DD'));
                        const end = moment(lastSegment?.format('YYYY-MM-DD'));
                        const duration = moment.duration(end.diff(start)).asDays();
                        const howManyDays = Math.ceil(duration) + 1;
                        return {
                            tripHash: tripRef.tripHash,
                            title: `${tripRef.city.name}, ${tripRef.city.country.name}`,
                            subtitle: `${howManyDays} ${howManyDays === 1 ? 'Day' : 'Days'}`,
                            image: cityImage,
                            alt: `${tripRef.city.name} cityscape`,
                            howManyDays
                        };
                    } catch (error) {
                        return null;
                    }
                });

                const results = await Promise.all(itineraryPromises);
                const validItineraries = results.filter((item): item is ItineraryData => item !== null);
                setItineraries(validItineraries);
            } catch (error) {
                console.error("Error fetching featured itineraries:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchFeaturedItineraries();
    }, []);


    return (
        <div className="w-full py-16 px-4">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <h2 className="text-2xl syne-fm">
                        {t('valai.featuredItineraries')}
                    </h2>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
                    </div>
                ) : itineraries.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-gray-500 text-lg">
                            {t('valai.noFeaturedItineraries')}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                        {itineraries.map((itinerary) => (
                            <div
                                key={itinerary.tripHash}
                                onClick={() => navigate(`/timeline/${itinerary.tripHash}!s`)}
                                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group"
                            >
                                <div className="relative h-44">
                                    <img
                                        src={itinerary.image}
                                        alt={itinerary.alt}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                                </div>

                                <div className="p-3">
                                    <h3 className="text-xl font-weight-500 text-gray-900 mb-2 font-18-h">
                                        {itinerary.title}
                                    </h3>
                                    <p className="text-gray-600 text-sm">
                                        {itinerary.subtitle}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
export default FeaturedItineraries;